import {
  memo,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback
} from 'react';
import {
  Box,
  CardHeader,
  MenuItem,
  Select,
  Typography,
  Fade,
  Stack,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Divider,
  CardContent
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SyncIcon from '@mui/icons-material/Sync';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { useDispatch, useSelector } from 'react-redux';
import { APIService } from 'src/api/api';
import { getAllSites } from 'src/redux/siteSlice';
import Loader from 'src/common/Loader';
import ReactECharts from 'src/common/ReactECharts';
import * as echarts from 'echarts';
import { getFuelQuantity } from 'src/utils/getInitials';

const renderSummaryFuelQuantity = (litres, gallons) => {
  const formattedValue = getFuelQuantity(litres, gallons ?? undefined);
  const conversionStart = formattedValue.indexOf(' (');

  if (conversionStart === -1) {
    return <span style={{ fontWeight: 800 }}>{formattedValue}</span>;
  }

  return (
    <>
      <span style={{ fontWeight: 800 }}>
        {formattedValue.slice(0, conversionStart)}
      </span>
      <span style={{ fontWeight: 400 }}>
        {formattedValue.slice(conversionStart).replace('gallons', 'gal')}
      </span>
    </>
  );
};

// --- Chart Configuration Helper ---
const getChartOption = (data, theme) => {
  if (!data || !data.datasets) return {};

  const series = data.datasets.map((dataset) => {
    let color = '#2563EB'; // Default
    if (dataset.label === 'Inward') color = '#10B981'; // Green
    if (dataset.label === 'Outward') color = '#2563EB'; // Blue
    if (dataset.label === 'Transfer') color = '#F59E0B'; // Orange

    return {
      name: dataset.label,
      type: 'line',
      smooth: true,
      showSymbol: true,
      symbol: 'circle',
      symbolSize: 8,
      data: dataset.data,
      lineStyle: {
        width: 3,
        color
      },
      itemStyle: {
        color,
        borderWidth: 2,
        borderColor: '#fff'
      },
      areaStyle: {
        opacity: 0.4,
        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
          { offset: 0, color },
          { offset: 1, color: alpha(color, 0.01) }
        ])
      },
      emphasis: {
        focus: 'series'
      },
      animationDelay: (idx) => idx * 100
    };
  });

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => params.map((item) => {
        const dataset = data.datasets[item.seriesIndex];
        const gallons = dataset?.data_gallons?.[item.dataIndex];
        return `${item.marker}${item.seriesName}: ${getFuelQuantity(item.value, gallons ?? undefined)}`;
      }).join('<br/>'),
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider,
      borderWidth: 1,
      textStyle: {
        color: theme.palette.text.primary,
        fontFamily: theme.typography.fontFamily,
        fontSize: 12
      },
      axisPointer: {
        type: 'line'
      }
    },
    legend: {
      data: data.datasets.map((d) => d.label),
      bottom: 0,
      icon: 'circle',
      itemGap: 24,
      textStyle: {
        color: theme.palette.text.secondary,
        fontSize: 12
      }
    },
    grid: {
      left: '2%',
      right: '2%',
      bottom: '15%',
      top: '5%',
      containLabel: true
    },
    xAxis: {
      type: 'category',
      data: data.labels,
      boundaryGap: false,
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      axisLabel: {
        color: theme.palette.text.secondary,
        fontSize: 12,
        margin: 14
      }
    },
    yAxis: {
      type: 'value',
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: theme.palette.divider
        }
      },
      axisLabel: {
        color: theme.palette.text.secondary,
        fontSize: 11
      }
    },
    series
  };
};

const SalesChart = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { allSites } = useSelector((state) => state.sites);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [selectedSite, setSelectedSite] = useState('all');
  const [totals, setTotals] = useState({
    inward: 0,
    outward: 0,
    transfer: 0,
    inward_gallons: null,
    outward_gallons: null,
    transfer_gallons: null
  });
  const isMountedRef = useRef(false);
  const requestIdRef = useRef(0);

  const siteData = useMemo(() => [
    { id: 'all', name: 'All Sites' },
    ...(allSites || [])
  ], [allSites]);

  const fetchChartData = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (isMountedRef.current) {
      setLoading(true);
    }

    try {
      const siteId = selectedSite === 'all' ? '' : selectedSite;
      const res = await APIService.getDashboardSales(siteId);

      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }

      if (res.success && res.result) {
        setChartData(res.result);

        const newTotals = {
          inward: 0,
          outward: 0,
          transfer: 0,
          inward_gallons: null,
          outward_gallons: null,
          transfer_gallons: null
        };

        if (res.result.datasets) {
          res.result.datasets.forEach((ds) => {
            const sum = ds.data.reduce((a, b) => a + Number(b), 0);
            const gallonSum = Array.isArray(ds.data_gallons)
              ? ds.data_gallons.reduce((a, b) => a + Number(b), 0)
              : null;
            if (ds.label === 'Inward') newTotals.inward = sum;
            if (ds.label === 'Outward') newTotals.outward = sum;
            if (ds.label === 'Transfer') newTotals.transfer = sum;
            if (ds.label === 'Inward') newTotals.inward_gallons = gallonSum;
            if (ds.label === 'Outward') newTotals.outward_gallons = gallonSum;
            if (ds.label === 'Transfer') newTotals.transfer_gallons = gallonSum;
          });
        }
        setTotals(newTotals);
      }
    } catch (error) {
      if (!isMountedRef.current || requestId !== requestIdRef.current) {
        return;
      }

      console.error('Dashboard sales error:', error.message);
    } finally {
      if (isMountedRef.current && requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [selectedSite]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  useEffect(() => {
    if (!allSites || allSites.length === 0) {
      dispatch(getAllSites());
    }
  }, [dispatch, allSites]);

  const handleSiteChange = (e) => {
    setSelectedSite(e.target.value);
  };

  const chartOption = useMemo(() => (
    getChartOption(chartData, theme)
  ), [chartData, theme]);

  return (
    <>
      <CardHeader
        sx={{
          p: 2.5,
          pb: 1.5,
          '& .MuiCardHeader-action': {
            m: 0
          }
        }}
        title={(
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
          >
            <Box
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LocalShippingIcon fontSize="medium" />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.2
                }}
              >
                FUEL TRANSACTIONS
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
              >
                Last 10 Days Activity
              </Typography>
            </Box>
          </Stack>
        )}
        action={(
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <Select
              name="site"
              variant="outlined"
              size="small"
              onChange={handleSiteChange}
              value={selectedSite}
              sx={{
                minWidth: 160,
                height: 36,
                bgcolor: '#F9FAFB',
                borderRadius: 2,
                fontSize: '0.813rem',
                fontWeight: 500,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(0,0,0,0.08)'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main'
                }
              }}
            >
              {siteData.map((item) => (
                <MenuItem
                  value={item.id}
                  key={item.id}
                >
                  {item.name}
                </MenuItem>
              ))}
            </Select>

            <Tooltip title="Refresh Data">
              <IconButton
                onClick={fetchChartData}
                size="small"
                sx={{
                  bgcolor: '#F9FAFB',
                  border: '1px solid',
                  borderColor: 'rgba(0,0,0,0.08)',
                  borderRadius: 2,
                  width: 36,
                  height: 36,
                  color: 'text.secondary',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    transform: 'rotate(180deg)'
                  }
                }}
              >
                <SyncIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      />

      <Divider
        sx={{
          borderStyle: 'dashed',
          mx: 2.5
        }}
      />

      {/* Quick Stats Summary Row */}
      {!loading && chartData && (
        <Stack
          direction="row"
          spacing={3}
          sx={{
            px: 3,
            py: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.02)
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box
              sx={{
                p: 0.5,
                bgcolor: alpha('#2563EB', 0.1),
                borderRadius: 1,
                color: '#2563EB'
              }}
            >
              <CallMadeIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  display: 'block'
                }}
              >
                Total Outward
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800 }}
              >
                {renderSummaryFuelQuantity(totals.outward, totals.outward_gallons)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box
              sx={{
                p: 0.5,
                bgcolor: alpha('#10B981', 0.1),
                borderRadius: 1,
                color: '#10B981'
              }}
            >
              <CallReceivedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  display: 'block'
                }}
              >
                Total Inward
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800 }}
              >
                {renderSummaryFuelQuantity(totals.inward, totals.inward_gallons)}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Box
              sx={{
                p: 0.5,
                bgcolor: alpha('#F59E0B', 0.1),
                borderRadius: 1,
                color: '#F59E0B'
              }}
            >
              <SwapHorizIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  display: 'block'
                }}
              >
                Total Transfer
              </Typography>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 800 }}
              >
                {renderSummaryFuelQuantity(totals.transfer, totals.transfer_gallons)}
              </Typography>
            </Box>
          </Box>
        </Stack>
      )}

      {/* Main Chart Area */}
      <CardContent
        sx={{
          p: 0,
          '&:last-child': {
            pb: 0
          }
        }}
      >
        <Box
          sx={{
            height: 340,
            width: '100%',
            position: 'relative',
            px: 1
          }}
        >
          {loading ? (
            <Box
              sx={{
                height: '100%',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Loader />
            </Box>
          ) : (
            <Fade
              in
              timeout={600}
            >
              <Box
                sx={{
                  height: '100%',
                  width: '100%'
                }}
              >
                <ReactECharts
                  options={chartOption}
                  className="csp-echarts csp-echarts--full"
                />
              </Box>
            </Fade>
          )}
        </Box>
      </CardContent>
    </>
  );
};

export default memo(SalesChart);
